import { useState, useEffect, useCallback, useRef } from "react";
import { makeRequest } from "../assets/functions_helper";

/**
 * useSubscriptionPlans
 *
 * Country-Based Pricing — v2 (backend-driven country)
 *
 * Data flow:
 *   1. Read user.country from localStorage (set at login time).
 *      If country is null → return countryMissing: true, skip price fetch.
 *   2. GET /subscriptions_router/get_prices_by_country?country_id={id}
 *      (no auth required)
 *   3. GET /subscriptions_router/plan_features  (requires auth)
 *   4. Merge prices + features by plan_tier, sorted by features.tier_rank.
 *
 * Security contract:
 *   Returns `trustedPricingIds` — a frozen Set<number|string> built exclusively
 *   from the IDs returned by step 2 for THIS user's country_id.
 *   Callers MUST validate that any pricing_id selected by the user is present
 *   in this Set before passing it to any checkout endpoint.
 *
 * Returns:
 *   { plans, currentTier, isLoading, error, refetch, countryMissing, trustedPricingIds }
 */

const LS_COUNTRY_KEY = "userCountry";

function readCountryFromStorage() {
  try {
    const raw = localStorage.getItem(LS_COUNTRY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function useSubscriptionPlans() {
  const [plans, setPlans]                       = useState([]);
  const [currentTier, setCurrentTier]           = useState(null);
  const [isLoading, setIsLoading]               = useState(true);
  const [error, setError]                       = useState(null);
  const [countryMissing, setCountryMissing]     = useState(false);
  // Frozen Set of pricing_ids obtained from the backend for the user's country.
  // Only IDs in this set are allowed through to checkout.
  const [trustedPricingIds, setTrustedPricingIds] = useState(() => Object.freeze(new Set()));

  // Track the country_id used for the last fetch so we can detect changes.
  const lastCountryIdRef = useRef(null);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // ── Step 1: Resolve country_id from localStorage ──────────────────────────
    const userCountry = readCountryFromStorage();
    const countryId   = userCountry?.id ?? null;

    if (!countryId) {
      // No country configured — surface the blocker flag, don't fetch prices.
      setCountryMissing(true);
      setPlans([]);
      setTrustedPricingIds(Object.freeze(new Set()));
      setIsLoading(false);
      return;
    }

    setCountryMissing(false);
    lastCountryIdRef.current = countryId;

    try {
      // ── Steps 2 & 3: Prices (no auth) + Features (auth) in parallel ─────────
      const [prices, featuresData] = await Promise.all([
        makeRequest(
          "GET",
          `subscriptions_router/get_prices_by_country?country_id=${countryId}`,
          null,
          "application/json",
          null,      // no auth needed for this endpoint
          null,
          null,
          true       // skip global loader
        ),
        makeRequest(
          "GET",
          "subscriptions_router/plan_features",
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          true
        ),
      ]);

      setCurrentTier(featuresData?.current_tier || null);

      // ── Build the trusted Set from the raw price list ──────────────────────
      // This set is the security perimeter: ONLY these IDs may reach checkout.
      const rawPricingIds = new Set();

      // ── Group prices by tier, normalise field names ─────────────────────────
      // Keep the monthly price if multiple billing cycles exist for a tier,
      // otherwise keep the first one encountered.
      const pricingByTier = {};
      if (Array.isArray(prices)) {
        prices.forEach((p) => {
          const id   = p.pricing_id;
          const tier = p.plan_tier?.toLowerCase();
          if (!tier) return;

          // Collect every valid id into the trusted set
          if (id != null) rawPricingIds.add(id);

          if (!pricingByTier[tier] || p.billing_type === "monthly") {
            pricingByTier[tier] = p;
          }
        });
      }

      // Freeze the trusted set so nothing can mutate it after creation.
      setTrustedPricingIds(Object.freeze(new Set(rawPricingIds)));

      // ── Merge prices + features, sorted by tier_rank ───────────────────────
      const planFeatures = featuresData?.plans || {};

      // Build the merged array from all tiers present in either source,
      // then sort ascending by features.tier_rank to respect backend ordering.
      const allTiers = Array.from(
        new Set([
          ...Object.keys(pricingByTier),
          ...Object.keys(planFeatures),
        ])
      );

      const merged = allTiers.map((tier) => {
        const raw     = pricingByTier[tier] || null;
        const features = planFeatures[tier] || null;

        // Normalise API field names → internal contract used throughout the app:
        //   pricing_id    → subscription_pricing_id
        //   price         → amount
        //   currency_type → currency
        //   billing_type  → billing_interval
        const pricing = raw
          ? {
              ...raw,
              subscription_pricing_id: raw.pricing_id,
              amount:           raw.price,
              currency:         raw.currency_type,
              billing_interval: raw.billing_type,
            }
          : null;

        return { tier, pricing, features };
      });

      // Sort by features.tier_rank ascending (fallback 999 keeps unknown tiers last)
      merged.sort(
        (a, b) => (a.features?.tier_rank ?? 999) - (b.features?.tier_rank ?? 999)
      );

      setPlans(merged);

    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setError(
        typeof err === "string"
          ? err
          : "Error al cargar los planes. Inténtalo de nuevo."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    currentTier,
    isLoading,
    error,
    refetch: fetchPlans,
    countryMissing,
    trustedPricingIds,
  };
}
